import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
  ViewChild,
  DestroyRef,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Compass from '@arcgis/core/widgets/Compass';
import NavigationToggle from '@arcgis/core/widgets/NavigationToggle';
import Zoom from '@arcgis/core/widgets/Zoom';
import Expand from '@arcgis/core/widgets/Expand';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { IStepOption, TourService } from 'ngx-ui-tour-core';
import { Observable, Subject } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import Fullscreen from '@arcgis/core/widgets/Fullscreen';
import Search from '@arcgis/core/widgets/Search';
import { MapService } from 'src/app/predictor/services/map.service';
import { LicenseStudyAreaServiceService } from 'src/app/predictor/services/license-study-area-service.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AddStudyAreaNameModalComponent } from 'src/app/predictor-enterprise-license/shared/components/add-study-area-name-modal/add-study-area-name-modal.component';
import { UserState } from 'src/app/state/user/user.state';
import { UserActions } from 'src/app/state/user/user.actions';
import {
  LicenseStudyAreas,
  PolygonCoordinatesStudyAreas,
} from 'src/app/predictor-enterprise-license/shared/interface/License-Study-Areas';
import { LoggedInLicenseDetails } from 'src/app/predictor-enterprise-license/shared/interface/Enterprise-License';

@Component({
  selector: 'app-create-new-study-area',
  templateUrl: './create-new-study-area.component.html',
  styleUrl: './create-new-study-area.component.scss',
  standalone: false,
})
export class CreateNewStudyAreaComponent implements OnInit {
  @Output() public mapLoadedEvent = new EventEmitter<boolean>();
  @ViewChild('mapViewNode', { static: true }) public mapViewEl!: ElementRef;

  @Select(UserState.userLicenseAccess) userLicenseAccess$!: Observable<
    LoggedInLicenseDetails[]
  >;

  licenseDetail: LoggedInLicenseDetails | undefined;

  licenseStudyAreaData: LicenseStudyAreas[] = [];

  polygonData: PolygonCoordinatesStudyAreas | undefined;

  ngUnsubscribe: Subject<void> = new Subject<void>();

  public view: __esri.SceneView | undefined;
  public estimatedCost = 0;
  public estimatedArea = 0;
  public remainingLicenseArea: number = 0;
  //public remainingLicenseAreaUnit = 'square-kilometers';
  public tierAreaLimit = 1500; // Example limit for Tier 1
  public totalStudyArea = 0;
  public currentStudyArea = 0;
  public areaUnit: __esri.AreaUnits = 'square-kilometers';
  public countryName = '';
  public isPolygonDeleted = false;
  public isSubmitted = false;
  public fullView = false;
  public show = false;
  public currentStep = 0;
  public zoomValue: any;
  public controller = new AbortController();
  public studyAreaGeometry: any;
  public isAreadrawn: boolean = false;
  public isCalculateComplete: boolean = false;
  public isLicenseExceedLimit: boolean = false;
  public static readonly license_Tolerance: number = -0.01;
  public isMapLoading = true;
  public showForm = false; // Flag to control form visibility
  public maxTierArea = 0;
  public newArea: boolean = true;

  // Only store minimal geometry
  public studyAreaRings: number[][][] | null = null;
  public studyAreaExtent: __esri.Extent | null = null;

  private sketchViewModel: any;
  private readonly featureLayer: FeatureLayer;
  private readonly eventListeners: (() => void)[] = [];

  private readonly tourService = inject(TourService<Object>);
  private readonly mapService = inject(MapService);
  private readonly fb = inject(FormBuilder);

  public studyAreaName: string = '';
  public isStudyAreaVisible: boolean = false;
  public stepsItem: MenuItem[] = [
    { label: 'Draw Area' },
    { label: 'Review Details' },
  ];
  private readonly destroyRef = inject(DestroyRef);
  public readonly steps: IStepOption[] = [
    /* your tour steps here */
  ];
  public isAvailableListClickable: boolean = true;


  constructor(
    private dialogService: DialogService,
    private readonly studyAreaService: LicenseStudyAreaServiceService,
    public activatedRoute: ActivatedRoute,
    private readonly store: Store,
    private readonly router: Router,
    private readonly messageService: MessageService
  ) {
    this.featureLayer = new FeatureLayer({
      url: 'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/World_Countries_(Generalized)/FeatureServer/0',
    });

    this.destroyRef.onDestroy(() => {
      console.log('Component destroyed');
      this.eventListeners.forEach((remove) => remove());
      this.sketchViewModel?.destroy?.();
      this.controller.abort();
      this.view?.graphics.removeAll();
      this.view?.destroy?.();
      this.studyAreaRings = null;
      this.studyAreaExtent = null;
      this.studyAreaService.setLicenseStudyAreaData(this.licenseStudyAreaData);
    });
  }

  ngOnInit(): void {
    this.initComponent();
  }

  private async initComponent(): Promise<void> {
    this.studyAreaService.currentStep$.subscribe((step) => {
      this.currentStep = 0;
    });

    // Subscribe to the licenses observable
    this.store.dispatch(new UserActions.GetLicenseAccess()).subscribe(() => {
      // Get the licenses from the store after state is updated
      const wrappedLicenses = this.store.selectSnapshot(
        UserState.userLicenseAccess
      );

      // Always fallback to empty array if undefined/null
      this.licenseDetail = (wrappedLicenses as any)?.$values ?? wrappedLicenses;

      // Now this.licenseAccess is guaranteed to be an array
      if (this.licenseDetail == null || // covers both null and undefined
        !this.licenseDetail?.licenseNumber
      ) {
        alert(
          'You do not have access to create a study area. Please contact your administrator.'
        );
        this.router.navigate(['/predictor']);
      } else {
        this.studyAreaService
          .getById(this.licenseDetail?.licenseNumber ?? '')
          .subscribe((data) => {
            this.licenseStudyAreaData = (data as any)?.$values ?? data ?? [];

            this.totalStudyArea = (this.licenseStudyAreaData ?? []).reduce(
              (sum, item) => sum + (item.areaMeasurement ?? 0),
              0
            );
            this.isStudyAreaVisible = true;
          });
      }
    });

    this.totalStudyArea = (this.licenseStudyAreaData || []).reduce(
      (sum, item) =>
        sum +
        (item.areaUnit == 'hectares'
          ? this.hectaresToAcres(item.areaMeasurement ?? 0)
          : item.areaMeasurement ?? 0),
      0
    );

    this.isMapLoading = true;
    const container = this.mapViewEl.nativeElement;
    const { view, graphicsLayer } = await this.mapService.initializeMapView(
      container
    );
    this.view = view;

    this.addUIComponents(view);
    this.sketchViewModel = await this.mapService.setupSketchViewModel(
      view,
      graphicsLayer
    );
    this.addEventListeners(this.sketchViewModel);

    this.isMapLoading = false;
    this.mapLoadedEvent.emit(true);
    this.studyAreaService.drawnAreaSource$.subscribe((drawnArea) => {
      this.loadPolygonForEditing(drawnArea, this.newArea);
    });
  }

  private addUIComponents(view: any) {
    view.ui.remove('zoom');
    view.ui.remove('navigation-toggle');
    view.ui.remove('compass');

    view.ui.add(new Zoom({ view, container: 'custom-zoom' }), 'top-left');
    view.ui.add(
      new NavigationToggle({ view, container: 'custom-navigation-toggle' }),
      'top-left'
    );
    view.ui.add(new Compass({ view, container: 'custom-compass' }), 'top-left');
    view.ui.add(
      new Fullscreen({ view, container: 'custom-fullscreen' }),
      'top-left'
    );
    view.ui.add(
      new Search({ view, container: 'search-location' }),
      'top-right'
    );

    view.ui.add('map-button-container', 'bottom-left');

    view.ui.add(
      new Expand({ view, container: 'custom-toggle-btn' }),
      'right-center'
    );
  }

  private addEventListeners(sketchViewModel: any) {
    const drawButtons = document.getElementsByClassName('starttool');
    Array.from(drawButtons).forEach((btn) => {
      const handler = (event: any) => {
        this.isPolygonDeleted = false;
        sketchViewModel.layer.removeAll();
        sketchViewModel.create(event.target.getAttribute('data-type'));
      };
      btn.addEventListener('click', handler);
      this.eventListeners.push(() => btn.removeEventListener('click', handler));
    });

    const cancelBtn = document.getElementById('cancel');
    const eyeBtn = document.getElementById('eye-view');

    if (cancelBtn) {
      const cancelHandler = () => this.clearDrawing(sketchViewModel);
      cancelBtn.addEventListener('click', cancelHandler);
      this.eventListeners.push(() =>
        cancelBtn.removeEventListener('click', cancelHandler)
      );
    }

    if (eyeBtn && this.view) {
      const eyeHandler = () => this.view?.goTo({ tilt: 0 });
      eyeBtn.addEventListener('click', eyeHandler);
      this.eventListeners.push(() =>
        eyeBtn.removeEventListener('click', eyeHandler)
      );
    }

    sketchViewModel.on('create', (event: any) => {
      if (event.state === 'complete') {
        sketchViewModel.update(event.graphic);
      }
    });

    sketchViewModel.on('update', async (event: any) => {
      await this.handleSketchUpdate(event);
    });
  }

  private clearDrawing(sketchViewModel: any) {
    this.remainingLicenseArea = this.remainingLicenseArea + this.estimatedArea;
    this.estimatedCost = 0;
    this.estimatedArea = 0;
    this.isPolygonDeleted = true;
    this.studyAreaRings = null;
    this.studyAreaExtent = null;
    this.polygonData = undefined;
    sketchViewModel.delete();
    this.isAreadrawn = false;
    this.currentStep = 0;
    this.isCalculateComplete = false;
    this.isLicenseExceedLimit = false;
  }

  private async handleSketchUpdate(event: any) {
    const graphic = event?.graphics?.[0];
    const geometry = graphic?.geometry;

    if (!geometry || this.isPolygonDeleted) return;

    this.studyAreaGeometry = geometry;
    this.studyAreaRings = geometry.rings;
    this.studyAreaExtent = geometry.extent ?? null;
    this.isAreadrawn = true;

    const polygoncoordinates: PolygonCoordinatesStudyAreas = {
      hasZ: geometry.hasZ ?? false,
      spatialReference: geometry.spatialReference,
      rings: geometry.rings,
    };
    this.polygonData = polygoncoordinates;
  }

  async onCalculateClick() {
    await this.calculateAndValidateArea(this.studyAreaGeometry);
  }

  private async calculateAndValidateArea(geometry: __esri.Geometry) {
    if (!geometry) return;

    try {
      // Step 1: Detect country
      const query = this.featureLayer.createQuery();
      query.geometry = geometry.extent;
      query.spatialRelationship = 'intersects';

      const response = await this.featureLayer.queryFeatures(query);
      const country = response.features[0]?.attributes?.COUNTRY ?? 'Unknown';
      this.countryName = country;

      const unit = 'square-kilometers';

      this.areaUnit = unit;

      // Step 3: Calculate area
      const geometryEngine = await import(
        '@arcgis/core/geometry/geometryEngine'
      );
      const area = geometryEngine.geodesicArea(
        geometry as __esri.Polygon,
        this.areaUnit
      );

      this.estimatedArea = this.roundToTwoDecimals(area);

      // Step 4: Convert drawn area to km²
      // const areaInKm2 = unit === 'acres' ? area * 0.00404686 : area * 0.01;

      // Step 5: Validate against remaining license area
      if (this.licenseDetail != null && this.licenseDetail?.maxArea != null) {
        this.maxTierArea =
          this.licenseDetail?.maxArea == -1
            ? -1
            : this.licenseDetail.maxArea;
      }

      //const remainingAfterDraw = this.maxTierArea == -1 ? -1 : this.roundToTwoDecimals(this.maxTierArea - this.estimatedArea);
      this.currentStudyArea = this.estimatedArea;


      // Step 6: Show result
      this.remainingLicenseArea =
        this.maxTierArea == -1
          ? -1
          : this.roundToTwoDecimals(this.getRemainingStudyArea(
            this.maxTierArea,
            this.totalStudyArea,
            this.currentStudyArea
          ));
      console.log('Remaining License Area:', this.remainingLicenseArea);

      if (
        this.remainingLicenseArea != -1 &&
        this.remainingLicenseArea <=
        CreateNewStudyAreaComponent.license_Tolerance
      ) {
        this.isLicenseExceedLimit = true;
        this.showForm = false; // example flag controlling form visibility
        //this.store.dispatch(
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Sorry, your study area has exceeded the limit of your tier license. Please try again or contact support to upgrade',
          key: 'mapView',
        });
        //);
      } else {
        this.isCalculateComplete = true;
        this.showForm = true; // example flag controlling form visibility
        //this.remainingLicenseArea = remainingAfterDraw;

        this.submitPartialDetails();
        const ref: DynamicDialogRef = this.dialogService.open(
          AddStudyAreaNameModalComponent,
          {
            width: '620px',
            dismissableMask: true,
            styleClass: 'bottom-rounded basin-font-primary ',
            header: 'Save Study Area',
            appendTo: this.mapViewEl.nativeElement,
            data: {
              remainingLicenseArea: this.remainingLicenseArea,
              currentStudyArea: this.currentStudyArea,
              areaUnit: this.areaUnit,
              licenseNumber: this.licenseDetail?.licenseNumber,
            },
          }
        );
        ref.onClose.subscribe((formValue: string) => {
          if (!formValue) return;

          // ✅ Move to next step in the flow
          //this.changeStep();  // or 'summary', 'review', etc.
        });
      } //If condition
    } catch (error) {
      console.error('Error calculating area:', error);
      //this.store.dispatch(

      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'An error occurred while calculating the study area. Please try again.' +
          error,
        key: 'mapView',
      });
      //);
    }
  }

  public zoomToPolygonFromRings(): void {
    if (!this.studyAreaRings || !this.view) return;

    const polygon = new Polygon({
      rings: this.studyAreaRings,
      spatialReference: { wkid: 3857 },
    });

    this.view.goTo(polygon).catch(console.warn);
    this.view.graphics.removeAll();

    this.view.graphics.add(
      new Graphic({
        geometry: polygon,
        symbol: {
          type: 'simple-fill',
          color: [255, 255, 0, 0.3],
          outline: { color: [255, 128, 0], width: 2 },
        },
      })
    );
  }

  public onStudyAreaClick(selectedArea: any) {
    const area = selectedArea;
    if (!selectedArea || !this.view) return;

    if (!area.polygon) {
      console.warn('No polygon data available for selected area.');
      return;
    }

    let parsedPolygon: any;
    try {
      parsedPolygon = JSON.parse(area.polygon);
    } catch (e) {
      console.error('Polygon JSON parsing failed:', e);
      return;
    }

    const points: number[][][] = parsedPolygon.rings?.[0];
    if (!points || !Array.isArray(points) || points.length === 0) {
      console.warn('Invalid or empty rings array in polygon.');
      return;
    }
    this.newArea = false
    this.loadPolygonForEditing(parsedPolygon, this.newArea);
  }

  changeStep() {
    this.currentStep = 1;
    this.studyAreaService.setStep(1);
  }

  private getRemainingStudyArea(
    tierLimit: number,
    totalStudyArea: number,
    currentStudyArea: number
  ): number {
    return tierLimit - (totalStudyArea + currentStudyArea);
  }

  submitPartialDetails() {
    let userProfile = this.store.selectSnapshot(UserState.userProfileName);
    if (!this.licenseDetail) return;
    const partialDetails: Partial<LicenseStudyAreas> = {
      licenseNumber: this.licenseDetail?.licenseNumber,
      createdBy: userProfile.firstName + ' ' + userProfile.lastName,
      remainingArea: this.remainingLicenseArea,
      areaUnit: this.areaUnit as string,
      currentSize: this.currentStudyArea,
      polygon: JSON.stringify(this.polygonData),
      countryName: this.countryName,
      emailAddress: userProfile.email,
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      phoneNumber: '',
      areaMeasurement: this.currentStudyArea,
      // add other fields user fills here
    };

    this.studyAreaService.setPartialStudyAreaDetails(partialDetails);
    this.studyAreaService.setDrawnAreaDetails(this.polygonData);
  }

  roundToTwoDecimals(num: number): number {
    return Math.round(num * 100) / 100;
  }

  goToMainPage() {
    this.router.navigate(['/predictor']);
  }

  convertSquareKmToUnit(
    areaInSqKm: number,
    unit: 'acres' | 'hectares'
  ): number {
    if (unit === 'acres') {
      return Math.round(areaInSqKm * 247.105 * 100) / 100;
    } else if (unit === 'hectares') {
      return Math.round(areaInSqKm * 100) / 100;
    } else {
      throw new Error('Invalid unit. Please use "acre" or "hectare".');
    }
  }

  hectaresToAcres(areaInHectares: number): number {
    return Math.round(areaInHectares * 2.47105 * 100) / 100;
  }

  toggleStudyAreaPanel() {
    this.isStudyAreaVisible = !this.isStudyAreaVisible;
  }

  async loadPolygonForEditing(polygonData: PolygonCoordinatesStudyAreas, NewArea: boolean) {
    if (!this.view || !this.sketchViewModel) return;

    // Create Polygon geometry from stored rings
    const polygon = new Polygon({
      rings: polygonData.rings,
      spatialReference: polygonData.spatialReference || this.view.spatialReference,
      hasZ: polygonData.hasZ ?? false,
    });

    // Create a Graphic with your desired symbol
    const graphic = new Graphic({
      geometry: polygon,
      symbol: {
        type: 'simple-fill',
        color: [0, 0, 0, 0.3], // semi-transparent fill
        outline: {
          color: [0, 0, 0], // black outline
          width: 2,
        },
      },
    });

    // Clear existing graphics in SketchViewModel's layer
    this.sketchViewModel.layer.removeAll();

    // Add this graphic to the SketchViewModel's layer
    this.sketchViewModel.layer.add(graphic);

    // Start updating (editing) this graphic
    if (NewArea) {
      await this.sketchViewModel.update(graphic);
    }


    // Zoom to polygon
    if (polygon.extent) {
      this.view.goTo(polygon.extent.expand(1.5)).catch(console.warn);
    }

    // Update flags
    this.isAreadrawn = true;
    this.isCalculateComplete = true;
    this.showForm = true;
  }
}
